import useCategoryHook from "@/shared/hooks/useCategoryHook";
import useLabelsHook from "@/shared/hooks/useLabelsHook";
import useMenuItemHook from "@/shared/hooks/useMenuItemHook";
import useVatHook from "@/shared/hooks/useVatHook";
import { productStatus, targetsWithoutAll } from "@/shared/utils/constant";
import { MenuItems } from "@/shared/utils/enum";
import {
  allergenen,
  asArray,
  cleanCharFromObject,
  getLabels,
} from "@/shared/utils/helpers";
import { Product } from "@/shared/utils/interfaces";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useImmer } from "use-immer";

interface Options {
  defaultValue?: any;
  parentId?: number;
  isEdit?: boolean;
}

const useProductForm = ({ defaultValue, parentId, isEdit }: Options) => {
  const { productCreate, productUpdate, refetch } = useCategoryHook();
  const methods = useForm();
  const { reset } = methods;
  const { data: products } = useMenuItemHook<Product[]>(MenuItems.PRODUCT);

  const allLabels = useMemo(() => getLabels(products), [products]);
  const { labels, add } = useLabelsHook(allLabels);
  const { vats } = useVatHook();

  const defaultFieldValues = useMemo(() => {
    const defaultRecommendedProducts = asArray(
      defaultValue?.recommended_menu_item_id
    );
    return {
      is_18_plus: defaultValue?.is_18_plus,
      is_free_product: defaultValue?.is_free_product ?? false,
      parentId: parentId,
      is_active:
        defaultValue?.is_active !== undefined ? defaultValue?.is_active : true,
      vat_enum: defaultValue?.vat_enum || vats[1]?.id,
      tags: allLabels.filter((label) =>
        defaultValue?.label?.includes(label.value)
      ),
      allergen:
        asArray(defaultValue?.allergen).map((al) =>
          allergenen.find((a) => a.id === al)
        ) || [],
      product: products?.filter((i) =>
        defaultRecommendedProducts.includes(i.item_id)
      ),
      status_enum: defaultValue?.status_enum
        ? productStatus.find((i) => i.value === defaultValue?.status_enum)
        : productStatus[0],
      garnishes: defaultValue?.sub_content || [],
      targets: defaultValue?.target
        ? asArray(defaultValue?.target)?.map((id) =>
            targetsWithoutAll.find((i) => i.value === id)
          )
        : targetsWithoutAll,
    };
  }, [defaultValue, products, parentId]);

  const [fields, setFields] = useImmer(defaultFieldValues);

  useEffect(() => {
    if (productUpdate.isError) {
      reset();
    }
    setFields(defaultFieldValues);
  }, [productUpdate.isError, defaultFieldValues]);

  const toSubmitData = (data) => {
    const pricePerTarget = cleanCharFromObject(data.price_per_target_value);
    return {
      ...defaultValue,
      ...data,
      price: fields.is_free_product ? "0.00" : data.price,
      preparation_time: data?.preparation_time
        ? data?.preparation_time
        : undefined,
      is_18_plus: fields.is_18_plus,
      is_free_product: fields.is_free_product,
      parentId: fields.parentId,
      is_active: fields.is_active,
      vat_enum: fields.vat_enum,
      tags: undefined,
      label: fields.tags.map((tag) => tag.value),
      allergen: fields?.allergen?.map((v) => v.id),
      status_enum: fields.status_enum.value,
      recommended_menu_item_id: fields?.product?.map((p) => p.item_id),
      product: undefined,
      garnishes: undefined,
      topping_categories: fields?.garnishes?.map((i) => i.item_id),
      item_type: "2",
      target: fields?.targets?.map((i) => i?.value),
      sub_content: undefined,
      price_per_target_value:
        fields.is_free_product && pricePerTarget
          ? Object.fromEntries(
              Object.keys(pricePerTarget).map((target) => [target, "0.00"])
            )
          : pricePerTarget,
      vat_enum_per_target: cleanCharFromObject(data.vat_enum_per_target),
      back_ground_color_per_target: cleanCharFromObject(
        data.back_ground_color_per_target,
        "%"
      ),
      text_color_per_target: cleanCharFromObject(
        data.text_color_per_target,
        "%"
      ),
    };
  };

  /** Saves the product; resolves after the mutation succeeds. */
  const save = (data) => {
    add(fields.tags);
    const submitData = toSubmitData(data);
    if (isEdit) {
      return productUpdate.mutateAsync(submitData);
    }
    return productCreate.mutateAsync({
      ...submitData,
      position: 0,
      parentId: fields.parentId ?? parentId,
    });
  };

  return {
    methods,
    fields,
    setFields,
    labels,
    vats,
    save,
    refetch,
    productCreate,
    productUpdate,
    isPending: productCreate.isPending || productUpdate.isPending,
    isError: productCreate.isError || productUpdate.isError,
  };
};

export default useProductForm;
